/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { camelCase } from 'lodash-es';
import { Observable, of, map, throwError } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { AlertFlashService } from '@callrail/looky/ui';

import { BulkIntegrationGroup } from '../../models/bulk-integration-group.model';
import { Integration as Vendor } from '../../models/integration.model';
import { LongRunningTask } from '../../models/long-running-task.model';
import { CurrentContextService } from '../current-context/current-context.service';
import { BulkIntegrationAuthorizeParams } from './bulk-integration-authorize-params.interface';
import { BulkIntegrationAuthorizeResponse } from './bulk-integration-authorize-response.interface';
import { BulkIntegrationGroupShowParams } from './bulk-integration-group-show-params.interface';
import { BulkIntegrationGroupUpdateParams } from './bulk-integration-group-update-params.interface';
import { BulkIntegrationRefreshParams } from './bulk-integration-refresh-params.interface';
import {
  IntegrationState as Integration,
  IntegrationStateValue as IntegrationState,
} from './integration-state.model';
import { IntegrationBackendServiceInterface } from './integration.backend.service.interface';
import { FetchVendorResponse } from '@callrail/inti/src/inti/proto/vendor';
import {
  CreateIntegrationRequest,
  AuthorizeIntegrationRequest,
  CreateIntegrationResponse,
  AuthorizeIntegrationResponse,
  IntegrationType,
  IntegrationState as IntiIntegrationState,
  UpdateIntegrationRequest,
  UpdateIntegrationResponse,
  FetchIntegrationResponse,
} from '@callrail/inti/src/inti/proto/integration';
import legacyTransformFetchVendor from './legacy-transformers/fetch-vendor';
import { legacyTransformIntegration } from './legacy-transformers/integration';
import { camelizeKeys, removeNullValues } from './legacy-transformers/helpers';
import { IntegrationOptions } from './integration-options.interface';

/**
 * TERMINOLOGY
 *
 * Historically, `looky` still uses legacy names for these models that differ
 * from the newer terminology used in Inti and CallRail
 *
 *  +---------------------+-----------------------+
 *  | What Inti calls..   | Looky calls...        |
 *  +---------------------+-----------------------+
 *  | Vendor              | Integration           |
 *  | Integration         | IntegrationState      |
 *  | IntegrationState    | IntegrationStateValue |
 *  +---------------------+-----------------------+
 *
 * This class attempts to break that pattern by aliasing these imports to the
 * newer terms. But this is important to keep in mind in other `looky` classes
 * that may still use older terminology.
 */
@Injectable({
  providedIn: 'root',
})
export class IntiBffService implements IntegrationBackendServiceInterface {
  private readonly intiBffVersion = 'v1';

  constructor(
    private http: HttpClient,
    private context: CurrentContextService,
    private alertFlashService: AlertFlashService
  ) {}

  /**
   * ************************************
   * BEGIN: BACKWARDS COMPATIBLE API
   * ************************************
   *
   * This service class purposely has the same API (function definitions) as
   * `IntegrationService`.
   *
   * This was to make the transition to `Inti` easier. Based on whether `Inti`
   * is enabled for a specific integration (via a Flipper flag), we use either
   * the traditional `IntegrationService` or this service class. So it's
   * important they share they same public API.
   *
   * Internally, these methods forward to a newer API with newer names.
   *
   * Once all integrations are served out of Inti we can deprecate the old class
   * and also start modifying this back-wards compatible API as needed.
   */

  public authorize(integration: Integration): Observable<any> {
    return this.authorizeIntegration(integration).pipe(
      map((resp) => {
        // reformat to match legacy response
        return { redirect: resp };
      })
    );
  }

  public connectAccount(integration: Integration): Observable<Integration> {
    return this.connectIntegration(integration);
  }

  public getDefaultsFromIntegrationMetadata(integration: Integration): any {
    return this.buildDefaultIntegrationConfigs(integration);
  }

  public load(
    integrationId: number,
    companyId?: string,
    params?: string
  ): Observable<Integration> {
    return this.fetchIntegration(integrationId);
  }

  public loadIntegrationState(
    companyId: string,
    integrationName: string,
    integrationParams: string,
    integrationSafeName: string
  ): Observable<Integration> {
    return this.loadIntegration(
      companyId,
      integrationSafeName,
      integrationParams
    );
  }

  public type(params: {
    companyId: string;
    integrationName: string;
  }): Observable<Vendor> {
    return this.fetchVendor(params);
  }

  public update(
    integration: Integration,
    options?: IntegrationOptions
  ): Observable<Integration> {
    return this.updateIntegration(integration, options);
  }

  public save(integration: Integration): Observable<Integration> {
    return this.createIntegration(integration);
  }

  public loadBulkIntegrationGroup(
    params: BulkIntegrationGroupShowParams
  ): Observable<BulkIntegrationGroup> {
    // TBD
    return of({});
  }

  public updateBulkIntegrationGroup(
    params: BulkIntegrationGroupUpdateParams
  ): Observable<LongRunningTask> {
    // TBD
    return of({});
  }

  public authorizeBulk(
    params: BulkIntegrationAuthorizeParams
  ): Observable<BulkIntegrationAuthorizeResponse> {
    // TBD
    return of({ redirect: '' });
  }

  public refreshBulkProviderData(
    params: BulkIntegrationRefreshParams
  ): Observable<LongRunningTask> {
    // TBD
    return of({});
  }

  /**
   * ************************************
   * END: BACKWARDS COMPATIBLE API
   * ************************************
   */

  public buildDefaultIntegrationConfigs(integration: Integration): any {
    const vendorConfig = integration.class.config;

    /*
     * Create new hash where
     *  - The key is the property name
     *  - The value is the default value as read from the vendor config metadata
     */
    return Object.keys(vendorConfig).reduce((defaults, property) => {
      if (vendorConfig[property].type === 'array') {
        defaults[property] = [''];
      } else {
        defaults[property] = vendorConfig[property].default;
      }
      return defaults;
    }, {});
  }

  public connectIntegration(integration: Integration): Observable<Integration> {
    const companyMid = this.context.currentCompany.id;
    const url = `/integration-settings/v1/c/${companyMid}/integrations/connect`;
    return this.postCreateRequest(url, integration);
  }

  public createIntegration(integration: Integration): Observable<Integration> {
    const companyMid = this.context.currentCompany.id;
    const url = `/integration-settings/v1/c/${companyMid}/integrations/create`;
    return this.postCreateRequest(url, integration);
  }

  public fetchIntegration(integrationId: number): Observable<Integration> {
    const companyMid = this.context.currentCompany.id;
    const url = `/integration-settings/v1/c/${companyMid}/integrations/${integrationId}`;

    return this.http.get(url, { responseType: 'arraybuffer' }).pipe(
      map((response) => {
        var uint8 = new Uint8Array(response);
        const result = FetchIntegrationResponse.decode(uint8);
        return legacyTransformIntegration(result.integration);
      }),
      catchError((error) =>
        this.handleProtoError(FetchIntegrationResponse, error)
      )
    );
  }

  public fetchVendor(params: {
    companyId: string;
    integrationName: string;
  }): Observable<Vendor> {
    const enumIntType = this.safeNameToEnumIntType(params.integrationName);
    const integrationType = this.integrationTypeString(enumIntType);
    const url = `/integration-settings/v1/c/${params.companyId}/vendors/${integrationType}`;

    return this.http.get(url, { responseType: 'arraybuffer' }).pipe(
      map((response) => {
        var uint8 = new Uint8Array(response);
        const result = FetchVendorResponse.decode(uint8);
        return legacyTransformFetchVendor(result.vendor);
      }),
      catchError((error) => this.handleProtoError(FetchVendorResponse, error))
    );
  }

  public loadIntegration(
    companyId: string,
    integrationSafeName: string,
    integrationParams: string
  ): Observable<Integration> {
    /**
     * We first load the `Vendor` which also happens to return the Integration
     * instance for this `companyId` (if one exists).
     *
     * Based on that we either follow up with a second fetch to retrieve the
     * actual `Integration`, or return a blank/new `Integration`
     *
     * We use this 2 step approach because we need a lot of the `Vendor`
     * information fetched in the first call as part of our later logic.
     */
    const integrationName = integrationSafeName;
    return this.fetchVendor({ companyId, integrationName }).pipe(
      mergeMap((vendor: Vendor) => {
        if (!vendor?.enabled_id) {
          // No integration already exists. Return a new/blank record
          return of({
            config: {},
            disabled: true,
            state: 'disabled' as IntegrationState,
            class: vendor as any,
          });
        }

        return this.fetchIntegration(vendor.enabled_id).pipe(
          mergeMap((integration: Integration) => {
            integration.class = vendor as Integration['class'];
            integration.owner_name = null;
            integration.site_name = vendor.site_name;
            integration.white_label = vendor.white_label;

            return of(integration);
          })
        );
      })
    );
  }

  public updateIntegration(
    integration: Integration,
    options
  ): Observable<Integration> {
    const enumIntType = this.safeNameToEnumIntType(integration.class.safe_name);
    const integrationTypeStr = this.integrationTypeString(enumIntType);
    const companyMid = this.context.currentCompany.id;
    const url = `/integration-settings/v1/c/${companyMid}/integrations/${integration.id}`;

    // Build a config with camelized key names
    const config = removeNullValues(camelizeKeys(integration.config));
    const state = integration.disabled
      ? IntiIntegrationState.DISABLED
      : options?.state
        ? options?.state
        : IntiIntegrationState.ACTIVE;

    // Build a payload conforming to `inti.proto.UpdateIntegrationRequest`
    const message = {
      uuid: String(integration.id),
      state,
      updaterMid: Number(this.context.currentUser.id),
      ingestionEnabled: integration.ingestion_enabled,
      schedulingEnabled: integration.scheduling_enabled,
      config: {
        [`${camelCase(integrationTypeStr)}Attributes`]: config,
      },
    };
    const payload = UpdateIntegrationRequest.encode(message).finish();

    return this.http
      .put(url, Array.prototype.toString.call(payload), {
        responseType: 'arraybuffer',
      })
      .pipe(
        map((response) => {
          var uint8 = new Uint8Array(response);
          const result = UpdateIntegrationResponse.decode(uint8);
          return legacyTransformIntegration(result.integration);
        }),
        catchError((error) =>
          this.handleProtoError(UpdateIntegrationResponse, error)
        )
      );
  }

  public authorizeIntegration(integration: Integration): Observable<any> {
    const companyMid = this.context.currentCompany.id;
    const url = `/integration-settings/v1/c/${companyMid}/integrations/authorize`;
    const enumIntType = this.safeNameToEnumIntType(integration.class.safe_name);

    // Build a payload conforming to `inti.proto.AuthorizeIntegrationRequest`
    const message = {
      integrationType: enumIntType,
      agencyMid: Number(this.context.currentAgency.id),
      companyMid: Number(this.context.currentCompany.id),
      authorizerMid: Number(this.context.currentUser.id),
      authorizerRid: this.context.currentUser.resource_id,
      hipaa: Boolean(integration.config.hipaa_confirmation),
      resellerPartner: this.context.currentAgency.reseller_partner || '',
    };

    const payload = AuthorizeIntegrationRequest.encode(message).finish();

    return this.http
      .post(url, Array.prototype.toString.call(payload), {
        responseType: 'arraybuffer',
      })
      .pipe(
        map((response) => {
          var uint8 = new Uint8Array(response);
          const result = AuthorizeIntegrationResponse.decode(uint8);
          return result.authorizeUrl;
        }),
        catchError((error) =>
          this.handleProtoError(AuthorizeIntegrationResponse, error)
        )
      );
  }

  private handleProtoError(response_type: any, error: HttpErrorResponse) {
    var uint8 = new Uint8Array(error.error);
    const result = response_type.decode(uint8);

    this.alertFlashService.addDanger(result?.result?.error);
    return throwError(() => new Error(result?.result?.error));
  }

  // this returns the all-caps string version of the enum value
  private integrationTypeString(int_type: IntegrationType) {
    return IntegrationType[int_type];
  }

  private postCreateRequest(
    url: string,
    integration: Integration
  ): Observable<Integration> {
    const enumIntType = this.safeNameToEnumIntType(integration.class.safe_name);
    const integrationTypeStr = this.integrationTypeString(enumIntType);
    const companyMid = this.context.currentCompany.id;

    const config = removeNullValues(camelizeKeys(integration.config));
    const message = {
      integrationType: enumIntType,
      agencyMid: Number(this.context.currentAgency.id),
      companyMid: Number(companyMid),
      creatorMid: Number(this.context.currentUser.id),
      disabled: false,
      config: {
        [`${camelCase(integrationTypeStr)}Attributes`]: config,
      },
      hipaa: Boolean(integration.config.hipaa_confirmation),
      resellerPartner: this.context.currentAgency.reseller_partner || '',
      ingestionEnabled: integration.ingestion_enabled,
    };
    const payload = CreateIntegrationRequest.encode(message).finish();

    return this.http
      .post(url, Array.prototype.toString.call(payload), {
        responseType: 'arraybuffer',
      })
      .pipe(
        map((response) => {
          var uint8 = new Uint8Array(response);
          const result = CreateIntegrationResponse.decode(uint8);
          return legacyTransformIntegration(result.integration);
        }),
        catchError((error) =>
          this.handleProtoError(CreateIntegrationResponse, error)
        )
      );
  }

  // by default, most safe names map 1:1 to the enum value
  private safeNameToEnumIntType(safe_name: string) {
    if (safe_name == 'optimizely_x') {
      return IntegrationType.OPTIMIZELY;
    } else {
      return IntegrationType[safe_name.toUpperCase()];
    }
  }
}
